import { useQuery } from '@tanstack/react-query';
import type { QuickRangeId } from 'common/components';
import { getDashboardData, DashboardDataParams } from '../../api/getDashboardData';
import type { CallDirectionType } from 'types/call.type';
import { queryKeys } from '../queryKeys';
import { startOfDay, endOfDay } from 'date-fns';

// export type DateRangeType = 'today' | 'yesterday' | 'last7Days' | 'last30Days' | 'custom';
export const DateRangeType = {
    today: 'today',
    yesterday: 'yesterday',
    last7Days: 'last7Days',
    last30Days: 'last30Days',
    custom: 'custom',
} as const;

// Dynamic refetch interval based on quick range preset
const getRefetchInterval = (quickRangeId: QuickRangeId | null) => {
    // Only auto-refresh for today's data, every 5 minutes
    return quickRangeId === 'today' ? 300000 : false; // 5 minutes (300000ms) or disabled
};

export const useGetDashboardData = (params: {
    startDate: Date;
    endDate: Date;
    quickRangeId: QuickRangeId | null;
    selectedWorkflow: string;
    callDirection?: CallDirectionType;
}) => {
    const localStartDate = startOfDay(params.startDate);
    const localEndDate = endOfDay(params.endDate);

    const dashboardParams: DashboardDataParams = {
        startDate: localStartDate.toISOString(),
        endDate: localEndDate.toISOString(),
        ...(params.selectedWorkflow &&
            params.selectedWorkflow !== 'All Workflows' && { workflowId: params.selectedWorkflow }),
        ...(params.callDirection && params.callDirection !== 'ALL' && { callDirection: params.callDirection }),
    };

    const { data, isLoading, error, refetch, isRefetching } = useQuery({
        queryKey: queryKeys.dashboardData(dashboardParams),
        queryFn: () => getDashboardData(dashboardParams),
        refetchInterval: getRefetchInterval(params.quickRangeId),
    });

    return {
        dashboardData: data,
        isDashboardDataLoading: isLoading || isRefetching,
        dashboardDataError: error,
        refetchDashboardData: refetch,
    };
};
